import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaMedal,
} from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { GiPodium } from "react-icons/gi";

import {
  formatPalmaresShortDate,
  getRankBadgeClass,
  getRankLabel,
  sortPalmaresItems,
} from "../utils/palmaresUtils";

function PalmaresPodiumCarousel({ palmares = [], onImageClick, onOpenLink }) {
  const podiumItems = useMemo(() => {
    return sortPalmaresItems(
      palmares.filter((item) => Boolean(item?.podium_palmares)),
      "dateDesc",
    );
  }, [palmares]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [podiumItems.length]);

  if (!podiumItems.length) return null;

  const currentItem = podiumItems[currentIndex];

  const goPrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? podiumItems.length - 1 : prev - 1,
    );
  };

  const goNext = () => {
    setCurrentIndex((prev) =>
      prev === podiumItems.length - 1 ? 0 : prev + 1,
    );
  };

  return (
    <section className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
      <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600 dark:text-amber-200">
            Galerie podiums
          </p>

          <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
            Photos de podium
          </h2>
        </div>

        <p className="text-sm font-bold text-slate-500 dark:text-white/50">
          {currentIndex + 1} / {podiumItems.length}
        </p>
      </div>

      <div className="grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
        <button
          type="button"
          onClick={() => onImageClick(currentItem.podium_palmares)}
          className="group relative min-h-[280px] overflow-hidden bg-slate-950 text-left sm:min-h-[360px]"
          title="Agrandir l'image du podium"
        >
          <img
            src={currentItem.podium_palmares}
            alt={currentItem.titre_palmares || "Podium"}
            className="absolute inset-0 h-full w-full object-cover opacity-90 transition duration-700 group-hover:scale-105"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

          <div className="absolute bottom-5 left-5 right-5 text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur-xl">
              <GiPodium />
              Voir en grand
            </span>
          </div>
        </button>

        <div className="flex flex-col justify-between p-5 sm:p-6">
          <div>
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${getRankBadgeClass(
                currentItem.classement_palmares,
              )}`}
            >
              <FaMedal />
              {getRankLabel(currentItem.classement_palmares)}
            </span>

            <h3 className="mt-4 font-titre text-3xl font-bold leading-tight text-slate-950 dark:text-white">
              {currentItem.titre_palmares || "Résultat sans titre"}
            </h3>

            <p className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-white/50">
              <FaCalendarAlt />
              {formatPalmaresShortDate(currentItem.date_palmares)}
            </p>

            <p className="mt-5 line-clamp-5 text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
              {currentItem.observations_palmares ||
                "Aucune observation renseignée pour ce podium."}
            </p>

            {currentItem.tableau_palmares && (
              <button
                type="button"
                onClick={() => onOpenLink(currentItem.tableau_palmares)}
                className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5"
              >
                <FaTableList />
                Ouvrir le tableau
                <FaExternalLinkAlt className="text-[10px]" />
              </button>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-5 dark:border-white/10">
            <button
              type="button"
              onClick={goPrevious}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
              aria-label="Podium précédent"
            >
              <FaChevronLeft />
            </button>

            <div className="flex flex-1 justify-center gap-2 overflow-hidden">
              {podiumItems.slice(0, 8).map((item, index) => (
                <button
                  key={item.id || `${item.event_palmares_id}-${index}`}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-12 w-12 shrink-0 overflow-hidden rounded-2xl border transition ${
                    index === currentIndex
                      ? "border-amber-400 ring-4 ring-amber-100 dark:ring-amber-500/10"
                      : "border-slate-200 opacity-70 hover:opacity-100 dark:border-white/10"
                  }`}
                  title={item.titre_palmares || "Podium"}
                >
                  <img
                    src={item.podium_palmares}
                    alt={item.titre_palmares || "Podium"}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
              aria-label="Podium suivant"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PalmaresPodiumCarousel;